import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo } from 'react'
import { handleFetchResponse } from 'api/errors'
import { IErrors } from 'components/types/app'
import { useActions } from 'hooks/useActions'
import { useTypedSelector } from 'hooks/useTypedSelector'
import { debounce } from 'lodash'
import { useGetAddressMutation } from 'modules/customer/api'
import { useFindDepartureDeliveryTypeMutation, useFindDestinationDeliveryTypeMutation } from 'modules/deliveryInfo/api'
import { DeliveryTypeEnum, TDeliveryTypeRequestData } from 'modules/deliveryInfo/types'
import { TOrderCreatePoint } from 'modules/order/types/orderCreateTypes'
import {
  useFindTerminalDepartureItemByCoordinatesMutation,
  useFindTerminalDepartureListByCoordinatesMutation,
  useFindTerminalDestinationItemByCoordinatesMutation,
  useFindTerminalDestinationListByCoordinatesMutation
} from 'modules/terminal/api'
import { TScheduleDay, TTerminal } from 'modules/terminal/types'
import { CourierDeliveryInfo, OrderItemClient } from 'schema'
import { store } from 'store/store'
import { reachGoal } from 'utils/yandexMetrika'

import { OrderCreateFormRouteItem } from './OrderCreateFormRouteItem/OrderCreateFormRouteItem'

type TProps = {
  error: any
  name: string
  onChange: (event: TOrderCreatePoint) => void
  pointType: CourierDeliveryInfo.pointType
  searchAddressValue: string
  setSearchAddressValue: Dispatch<SetStateAction<string>>
  value: TOrderCreatePoint
  className?: string
  customerId?: string
  customerTypeValue?: OrderItemClient.type
  departureTerminalId?: string
  initial?: TOrderCreatePoint
  isGuest?: boolean
  setDepartureTerminalId?: Dispatch<SetStateAction<string>>
  setDepartureTerminalSchedule?: Dispatch<SetStateAction<TScheduleDay[] | undefined>>
  setDestinationTerminalId?: Dispatch<SetStateAction<string>>
  setDestinationTerminalSchedule?: Dispatch<SetStateAction<TScheduleDay[] | undefined>>
  setPointType?: Dispatch<SetStateAction<DeliveryTypeEnum | undefined>>
}

export const OrderCreateFormRoute: FC<TProps> = (props) => {
  const {
    initial,
    name,
    isGuest,
    className,
    pointType,
    customerId,
    value,
    onChange,
    error,
    searchAddressValue,
    setSearchAddressValue,
    customerTypeValue,
    departureTerminalId,
    setDepartureTerminalId,
    setDestinationTerminalId,
    setPointType,
    setDepartureTerminalSchedule,
    setDestinationTerminalSchedule
  } = props
  const { isDadataDisable, fromPoint, toPoint } = useTypedSelector((state) => state.orderAddReducer)
  const {
    clearOrderAddStore,
    setOrderAddIsDaDataDisable,
    setOrderAddErrorAddress,
    setOrderAddComment,
    setOrderSelectedTerminal,
    setOrderTerminals,
    setOrderAddIsCourier,
    setOrderAddIsAddressTerminalDelivery,
    setOrderAddIsAddressCourierDelivery,
    setOrderAddPointSelectedOption,
    setOrderAddPointDaDataSearchAddressValue
  } = useActions()

  const [findDepartureDeliveryType, { error: deliveryDepartureTypeError, isLoading: isDepartureDeliveryTypeLoading }] =
    useFindDepartureDeliveryTypeMutation()
  const [
    findDestinationDeliveryType,
    { error: deliveryDestinationTypeError, isLoading: isDestinationDeliveryTypeLoading }
  ] = useFindDestinationDeliveryTypeMutation()

  const [
    findTerminalDepartureListByCoordinates,
    { error: terminalDepartureListError, isLoading: isTerminalDepartureListLoading }
  ] = useFindTerminalDepartureListByCoordinatesMutation()
  const [
    findTerminalDestinationListByCoordinates,
    { error: terminalDestinationListError, isLoading: isTerminalDestinationListLoading }
  ] = useFindTerminalDestinationListByCoordinatesMutation()

  const [findTerminalDepartureItemByCoordinates] = useFindTerminalDepartureItemByCoordinatesMutation()
  const [findTerminalDestinationItemByCoordinates] = useFindTerminalDestinationItemByCoordinatesMutation()

  const [
    getDepartureAddress,
    { data: departureAddress, isLoading: isDepartureAddressLoading, error: departureAddressSuggestionError }
  ] = useGetAddressMutation()
  const [
    getDestinationAddress,
    { data: destinationAddress, isLoading: isDestinationAddressLoading, error: destinationAddressSuggestionError }
  ] = useGetAddressMutation()

  const debouncedGetDepartureAddress = useMemo(() => debounce(getDepartureAddress, 500), [getDepartureAddress])
  const debouncedGetDestinationAddress = useMemo(() => debounce(getDestinationAddress, 500), [getDestinationAddress])

  const createPoint = (pointType: CourierDeliveryInfo.pointType) => {
    const isSelectedCourier =
      pointType === CourierDeliveryInfo.pointType.DEPARTURE ? fromPoint.isCourier : toPoint.isCourier
    const selectedOption =
      pointType === CourierDeliveryInfo.pointType.DEPARTURE ? fromPoint.selectedOption : toPoint.selectedOption
    const selectedTerminal =
      pointType === CourierDeliveryInfo.pointType.DEPARTURE ? fromPoint.selectedTerminal : toPoint.selectedTerminal
    const comment = pointType === CourierDeliveryInfo.pointType.DEPARTURE ? fromPoint.comment : toPoint.comment

    return {
      ...value,
      terminal: selectedTerminal,
      terminalId: selectedTerminal?.id,
      isCourier: isSelectedCourier,
      addressDetail: isSelectedCourier
        ? {
            // @ts-ignore
            address: isDadataDisable ? selectedOption?.address : selectedOption?.value,
            latitude: selectedOption?.latitude,
            longitude: selectedOption?.longitude,
            // @ts-ignore
            fiasId: selectedOption?.fiasId,
            comment: isSelectedCourier && comment?.length ? comment : undefined
          }
        : undefined
    }
  }

  const handleSuccess = useCallback(
    (response: any) => {
      setOrderAddIsAddressCourierDelivery({ key: pointType, value: response.includes(DeliveryTypeEnum.courier) })
      setOrderAddIsAddressTerminalDelivery({ key: pointType, value: response.includes(DeliveryTypeEnum.terminal) })
    },
    [pointType, setOrderAddIsAddressCourierDelivery, setOrderAddIsAddressTerminalDelivery]
  )

  const handleFailure = useCallback(
    (errors?: IErrors) => {
      const isDeliveryTypeError = errors && `${pointType}.point.addressDetail.address` in errors

      setOrderAddIsAddressCourierDelivery({ key: pointType, value: false })
      setOrderAddIsAddressTerminalDelivery({ key: pointType, value: false })
      setOrderAddIsCourier({ key: pointType, value: undefined })

      if (isDeliveryTypeError) {
        const isDeparture = pointType === CourierDeliveryInfo.pointType.DEPARTURE
        const selectedOption = isDeparture ? fromPoint.selectedOption : toPoint.selectedOption
        // @ts-ignore
        const address = isDadataDisable ? selectedOption?.address : selectedOption?.value

        reachGoal({
          goal: 'NO_DELIVERY',
          params: {
            address: `${address} (${isDeparture ? 'откуда' : 'куда'})`
          }
        })
      }
    },
    [
      fromPoint.selectedOption,
      isDadataDisable,
      pointType,
      setOrderAddIsAddressCourierDelivery,
      setOrderAddIsAddressTerminalDelivery,
      setOrderAddIsCourier,
      toPoint.selectedOption
    ]
  )

  useEffect(() => {
    if (pointType !== CourierDeliveryInfo.pointType.DEPARTURE) {
      return
    }
    const departurePoint = createPoint(CourierDeliveryInfo.pointType.DEPARTURE)
    setTimeout(() => {
      // @ts-ignore
      onChange(departurePoint)
    }, 0)
  }, [fromPoint.selectedOption, fromPoint.isCourier, fromPoint.comment, fromPoint.selectedTerminal]) // eslint-disable-line

  useEffect(() => {
    if (pointType !== CourierDeliveryInfo.pointType.DESTINATION) {
      return
    }
    const destinationPoint = createPoint(CourierDeliveryInfo.pointType.DESTINATION)
    setTimeout(() => {
      // @ts-ignore
      onChange(destinationPoint)
    }, 0)
  }, [toPoint.selectedOption, toPoint.isCourier, toPoint.comment, toPoint.selectedTerminal]) // eslint-disable-line

  useEffect(() => {
    if (pointType !== CourierDeliveryInfo.pointType.DEPARTURE) {
      return
    }
    if (!fromPoint.selectedOption || !fromPoint?.selectedOption?.latitude) {
      return
    }

    findDepartureDeliveryType({
      customerId,
      latitude: fromPoint.selectedOption?.latitude ? fromPoint.selectedOption?.latitude : '',
      longitude: fromPoint.selectedOption?.longitude ? fromPoint.selectedOption?.longitude : '',
      // eslint-disable-next-line
      address: isDadataDisable
        ? // TODO разобраться откуда address
          // @ts-ignore
          fromPoint.selectedOption?.address
          ? // @ts-ignore
            fromPoint.selectedOption?.address
          : '' // @ts-ignore
        : fromPoint.selectedOption?.value
          ? // @ts-ignore
            fromPoint.selectedOption?.value
          : ''
    }).then(handleFetchResponse(handleSuccess, handleFailure))

    findTerminalDepartureListByCoordinates({
      customerId,
      latitude: fromPoint.selectedOption?.latitude ? fromPoint.selectedOption?.latitude : '',
      longitude: fromPoint.selectedOption?.longitude ? fromPoint.selectedOption?.longitude : '',
      // eslint-disable-next-line
      address: isDadataDisable
        ? // TODO разобраться откуда address
          // @ts-ignore
          fromPoint.selectedOption?.address
          ? // @ts-ignore
            fromPoint.selectedOption?.address
          : '' // @ts-ignore
        : fromPoint.selectedOption?.value
          ? // @ts-ignore
            fromPoint.selectedOption?.value
          : ''
    })
      .unwrap()
      .then((response) => {
        setOrderTerminals({ key: pointType, value: response.data.terminal })
        const fromPointSelectedTerminalId = fromPoint.selectedTerminal?.id
        if (!fromPointSelectedTerminalId) {
          return
        }
        if (response.data.terminal.some((terminalItem) => terminalItem.id === fromPointSelectedTerminalId)) {
          return
        }
        setOrderSelectedTerminal({ key: pointType, value: null })
      })
      .catch(() => {
        setOrderTerminals({ key: pointType, value: null })
        setOrderSelectedTerminal({ key: pointType, value: null })
      })
  }, [fromPoint.selectedOption, customerId, customerTypeValue]) // eslint-disable-line

  useEffect(() => {
    if (pointType !== CourierDeliveryInfo.pointType.DESTINATION) {
      return
    }
    if (!toPoint.selectedOption || !toPoint?.selectedOption?.latitude) {
      return
    }
    findDestinationDeliveryType({
      customerId,
      latitude: toPoint.selectedOption?.latitude ? toPoint.selectedOption?.latitude : '',
      longitude: toPoint.selectedOption?.longitude ? toPoint.selectedOption?.longitude : '',
      // eslint-disable-next-line
      address: isDadataDisable
        ? // TODO разобраться откуда address
          // @ts-ignore
          toPoint.selectedOption?.address
          ? // @ts-ignore
            toPoint.selectedOption?.address
          : '' // @ts-ignore
        : toPoint.selectedOption?.value
          ? // @ts-ignore
            toPoint.selectedOption?.value
          : '',
      departureTerminalId: departureTerminalId || undefined
    }).then(handleFetchResponse(handleSuccess, handleFailure))

    findTerminalDestinationListByCoordinates({
      customerId,
      latitude: toPoint.selectedOption?.latitude ? toPoint.selectedOption?.latitude : '',
      longitude: toPoint.selectedOption?.longitude ? toPoint.selectedOption?.longitude : '',
      // eslint-disable-next-line
      address: isDadataDisable
        ? // TODO разобраться откуда address
          // @ts-ignore
          toPoint.selectedOption?.address
          ? // @ts-ignore
            toPoint.selectedOption?.address
          : '' // @ts-ignore
        : toPoint.selectedOption?.value
          ? // @ts-ignore
            toPoint.selectedOption?.value
          : '',
      departureTerminalId: departureTerminalId || undefined
    })
      .unwrap()
      .then((response) => {
        if (response) {
          setOrderTerminals({ key: pointType, value: response.data.terminal })
          const toPointSelectedTerminalId = toPoint.selectedTerminal?.id
          if (!toPointSelectedTerminalId) {
            return
          }
          if (response.data.terminal.some((terminalItem) => terminalItem.id === toPointSelectedTerminalId)) {
            return
          }
          setOrderSelectedTerminal({ key: pointType, value: null })
        }
      })
      .catch(() => {
        setOrderTerminals({ key: pointType, value: null })
        setOrderSelectedTerminal({ key: pointType, value: null })
      })
  }, [toPoint.selectedOption, departureTerminalId, customerId, customerTypeValue]) // eslint-disable-line

  useEffect(() => {
    if (pointType !== CourierDeliveryInfo.pointType.DEPARTURE) {
      return
    }
    if (
      // @ts-ignore
      (fromPoint.isCourier && fromPoint.selectedOption?.fiasId && fromPoint.selectedOption?.fiasLevel >= 7) ||
      (isDadataDisable && fromPoint.isCourier)
    ) {
      findTerminalDepartureItemByCoordinates({
        customerId,
        latitude: fromPoint.selectedOption?.latitude ? fromPoint.selectedOption?.latitude : '',
        longitude: fromPoint.selectedOption?.longitude ? fromPoint.selectedOption?.longitude : '',
        // eslint-disable-next-line
        address: isDadataDisable
          ? // TODO разобраться откуда address
            // @ts-ignore
            fromPoint.selectedOption?.address
            ? // @ts-ignore
              fromPoint.selectedOption?.address
            : '' // @ts-ignore
          : fromPoint.selectedOption?.value
            ? // @ts-ignore
              fromPoint.selectedOption?.value
            : ''
      })
        .unwrap()
        .then((response: any) => {
          if (response.data.terminal) {
            setOrderSelectedTerminal({ key: pointType, value: response.data.terminal })
            setDepartureTerminalSchedule && setDepartureTerminalSchedule(response.data.terminal.schedule)
            setDepartureTerminalId && setDepartureTerminalId(response.data.terminal.id)
          }
        })
    }
  }, [fromPoint.selectedOption, customerId, customerTypeValue]) // eslint-disable-line

  useEffect(() => {
    if (pointType !== CourierDeliveryInfo.pointType.DESTINATION) {
      return
    }
    if (
      // @ts-ignore
      (toPoint.isCourier && toPoint.selectedOption?.fiasId && toPoint.selectedOption?.fiasLevel >= 7) ||
      (isDadataDisable && toPoint.isCourier)
    ) {
      findTerminalDestinationItemByCoordinates({
        customerId,
        latitude: toPoint.selectedOption?.latitude ? toPoint.selectedOption?.latitude : '',
        longitude: toPoint.selectedOption?.longitude ? toPoint.selectedOption?.longitude : '',
        // eslint-disable-next-line
        address: isDadataDisable
          ? // TODO разобраться откуда address
            // @ts-ignore
            toPoint.selectedOption?.address
            ? // @ts-ignore
              toPoint.selectedOption?.address
            : '' // @ts-ignore
          : toPoint.selectedOption?.value
            ? // @ts-ignore
              toPoint.selectedOption?.value
            : '',
        departureTerminalId: departureTerminalId || undefined
      })
        .unwrap()
        .then((response: any) => {
          if (response) {
            setOrderSelectedTerminal({ key: pointType, value: response.data.terminal })
            setDestinationTerminalSchedule && setDestinationTerminalSchedule(response.data.terminal.schedule)
            setDestinationTerminalId && setDestinationTerminalId(response.data.terminal.id)
          }
        })
    }
  }, [toPoint.selectedOption, departureTerminalId, customerId, customerTypeValue]) // eslint-disable-line

  useEffect(() => {
    if (pointType !== CourierDeliveryInfo.pointType.DEPARTURE) {
      return
    }
    if (!fromPoint.selectedTerminal?.id) {
      setDepartureTerminalId && setDepartureTerminalId('')
    }
  }, [fromPoint.selectedTerminal?.id]) // eslint-disable-line

  useEffect(() => {
    if (pointType !== CourierDeliveryInfo.pointType.DESTINATION) {
      return
    }
    if (!toPoint.selectedTerminal?.id) {
      setDestinationTerminalId && setDestinationTerminalId('')
    }
  }, [toPoint.selectedTerminal?.id]) // eslint-disable-line

  useEffect(
    () => () => {
      clearOrderAddStore()
    },
    // eslint-disable-next-line
    []
  )

  const isDeparture = pointType === CourierDeliveryInfo.pointType.DEPARTURE

  const sharedProperties = {
    name,
    pointType,
    errorAddress: isDeparture
      ? // @ts-ignore
        fromPoint.errorAddress || departureAddressSuggestionError?.message
      : // @ts-ignore
        toPoint.errorAddress || destinationAddressSuggestionError?.message,
    setErrorAddress: (value: string) => setOrderAddErrorAddress({ key: pointType, value }),
    searchAddressValue,
    setSearchAddressValue,
    error,
    isDadataDisable,
    setIsDadataDisable: setOrderAddIsDaDataDisable,
    customerTypeValue,
    customerId,
    setPointType,
    isGuest
  }

  const conditionalProperties = {
    initialValue: initial,
    address: isDeparture ? departureAddress : destinationAddress,
    isAddressLoading: isDeparture ? isDepartureAddressLoading : isDestinationAddressLoading,
    daDataSearchAddressValue: isDeparture ? fromPoint.daDataSearchAddressValue : toPoint.daDataSearchAddressValue,
    setDaDataSearchAddressValue: (value: string) =>
      setOrderAddPointDaDataSearchAddressValue({
        key: pointType,
        value
      }),
    debouncedGetAddress: isDeparture ? debouncedGetDepartureAddress : debouncedGetDestinationAddress,
    setSelectedOption: (option: TDeliveryTypeRequestData | null) =>
      // TODO понять откуда расхождение типов
      // @ts-ignore
      setOrderAddPointSelectedOption({ key: pointType, value: option }),
    isAddressCourierDelivery: isDeparture ? fromPoint.isAddressCourierDelivery : toPoint.isAddressCourierDelivery,
    isAddressTerminalDelivery: isDeparture ? fromPoint.isAddressTerminalDelivery : toPoint.isAddressTerminalDelivery,
    isDeliveryTypeLoading: isDeparture ? isDepartureDeliveryTypeLoading : isDestinationDeliveryTypeLoading,
    selectedOption: isDeparture ? fromPoint.selectedOption : toPoint.selectedOption,
    isCourier: isDeparture ? fromPoint.isCourier : toPoint.isCourier,
    setIsCourier: (isCourier: boolean | undefined) => setOrderAddIsCourier({ key: pointType, value: isCourier }),
    terminals: isDeparture ? fromPoint.terminals : toPoint.terminals,
    selectTerminal: isDeparture ? fromPoint.selectedTerminal : toPoint.selectedTerminal,
    handleSelectTerminal: (value: TTerminal | null) => setOrderSelectedTerminal({ key: pointType, value }),
    comment: isDeparture ? fromPoint.comment : toPoint.comment,
    setComment: (value: string) => setOrderAddComment({ key: pointType, value }),
    deliveryTypeError: isDeparture ? deliveryDepartureTypeError : deliveryDestinationTypeError,
    terminalListError: isDeparture ? terminalDepartureListError : terminalDestinationListError,
    isTerminalLoading: isDeparture ? isTerminalDepartureListLoading : isTerminalDestinationListLoading,
    setTerminalId: isDeparture ? setDepartureTerminalId : setDestinationTerminalId,
    setDepartureTerminalSchedule: isDeparture ? setDepartureTerminalSchedule : setDestinationTerminalSchedule
  }

  const getCoordinatesAndAddress = (option: TDeliveryTypeRequestData | null) => {
    const latitude = option?.latitude || ''
    const longitude = option?.longitude || ''
    const address = isDadataDisable
      ? option?.address || ''
      : // @ts-ignore
        option?.value || ''

    return { latitude, longitude, address }
  }

  const handleResponse = (response: any, setter: any, setTerminalId?: any, setTerminalSchedule?: any) => {
    setter(response.data.terminal)

    if (setTerminalId) {
      setTerminalId(response.data.terminal.id)
    }
    if (setTerminalSchedule) {
      setTerminalSchedule && setTerminalSchedule(response.data.terminal.schedule)
    }
  }

  const handleTerminalListFind = (isDeparture: boolean, coordinatesAndAddress: any) => {
    const findFunction = isDeparture ? findTerminalDepartureListByCoordinates : findTerminalDestinationListByCoordinates

    findFunction({
      customerId,
      ...coordinatesAndAddress,
      departureTerminalId: departureTerminalId || undefined
    })
      .unwrap()
      .then((response) =>
        handleResponse(response, (value: any) => {
          setOrderTerminals({ key: pointType, value })
        })
      )
      .catch(() => {
        setOrderTerminals({ key: pointType, value: null })
        setOrderSelectedTerminal({ key: pointType, value: null })
      })
  }

  const handleTerminalItemFind = (isDeparture: boolean, coordinatesAndAddress: any) => {
    const findFunction = isDeparture ? findTerminalDepartureItemByCoordinates : findTerminalDestinationItemByCoordinates

    const setTerminalId = isDeparture ? setDepartureTerminalId : setDestinationTerminalId

    const setTerminalSchedule = isDeparture ? setDepartureTerminalSchedule : setDestinationTerminalSchedule

    const setTerminal = (terminal: TTerminal | null) => {
      const fromPointIsCourier = store.getState().orderAddReducer.fromPoint.isCourier
      const toPointIsCourier = store.getState().orderAddReducer.toPoint.isCourier
      if (pointType === CourierDeliveryInfo.pointType.DEPARTURE) {
        if (!fromPointIsCourier) {
          return
        }
        setOrderSelectedTerminal({ key: pointType, value: terminal })

        return
      }
      if (!toPointIsCourier) {
        return
      }
      setOrderSelectedTerminal({ key: pointType, value: terminal })
    }
    findFunction({
      customerId,
      ...coordinatesAndAddress,
      departureTerminalId: departureTerminalId || undefined
    })
      .unwrap()
      .then((response) => handleResponse(response, setTerminal, setTerminalId, setTerminalSchedule))
  }

  const handleChange = async (isCourier: boolean) => {
    const isDeparture = pointType === CourierDeliveryInfo.pointType.DEPARTURE
    const selectedOption = isDeparture ? fromPoint.selectedOption : toPoint.selectedOption
    // TODO понять откуда расхождение типов
    // @ts-ignore
    const coordinatesAndAddress = getCoordinatesAndAddress(selectedOption)

    if (!isCourier) {
      handleTerminalListFind(isDeparture, coordinatesAndAddress)
    }

    const isCourierCondition =
      // @ts-ignore
      (isCourier && selectedOption?.fiasId && selectedOption?.fiasLevel >= 7) ||
      (isDadataDisable && isCourier) ||
      (isCourier && initial?.addressDetail?.address === selectedOption?.address && !initial?.addressDetail?.fiasId)

    if (initial?.addressDetail?.address && initial?.addressDetail?.address === selectedOption?.address) {
      const addresses = isDeparture
        ? await getDepartureAddress(selectedOption.address).unwrap()
        : await getDestinationAddress(selectedOption.address).unwrap()

      const [address] = addresses || []
      const option = {
        ...selectedOption,
        fiasId: address?.fiasId,
        fiasLevel: address?.fiasLevel,
        latitude: address?.latitude,
        longitude: address?.longitude
      }

      if (option) {
        setOrderAddPointSelectedOption({ key: pointType, value: option })
      }
    }

    if (isCourierCondition) {
      handleTerminalItemFind(isDeparture, coordinatesAndAddress)
    }
  }

  return (
    <div className={className}>
      <h2 className="pb-2 pt-1 text-headline-sm text-on-surface md:pt-4">
        {pointType === CourierDeliveryInfo.pointType.DEPARTURE ? 'Откуда' : 'Куда'}
      </h2>
      {/* //TODO Убрать игнор, тут проблема в изначальном отсутствии типизации - исправить */}
      {/* @ts-ignore */}
      <OrderCreateFormRouteItem {...sharedProperties} {...conditionalProperties} name={name} onChange={handleChange} />
    </div>
  )
}
